/**
 * What each failure means to the user (IR-126 §4.5, phase 3.3).
 *
 * The apply layer answers with a machine-readable `error_code`, and the front
 * **branches on error class, never on terminal status**. A single "it failed"
 * path cannot carry the difference between a conflict the user must resolve, a
 * rule they cannot argue with, and a bug in our own code — and each wants a
 * different thing to happen next.
 *
 * ## The one that is not a message
 *
 * `stale` never becomes a toast. §5.1 requires the conflict to be **surfaced
 * with both values so the user chooses**, and §4.5 spells out why there is no
 * auto-rebase: task-level comparison cannot tell whether the concurrent change
 * touched the same field, so resubmitting against the new seq would silently
 * overwrite a change we cannot see. That is last-writer-wins with extra steps.
 *
 * Resubmitting is still allowed — but only because a human looked at both
 * values and said so. The difference is the whole point.
 *
 * ## Rollback is already done
 *
 * `submitTaskCommand` rolls back the optimistic value on any `failed` outcome
 * before this module sees it, so the "roll back" column of §4.5's table is not
 * this module's work. What is left is what to *say*, and whether to retry —
 * and for every class in that table the answer to retry is **no**.
 *
 * `task_not_found` is the one worth stating out loud: reaching a client at all
 * means the apply layer already deferred it `MAX_DEFERRALS` times (§5.5). A
 * front that resubmits loops forever against a task that was never created.
 */
import { logger } from '@/utils/logger'
import { useAppStore } from '@/stores/app'
import i18n from '@/plugins/i18n'
import type { TaskEditErrorCode } from './useTaskCommands'
import type { SequencedOutcome } from './useTaskSeq'

/**
 * What the caller should do about an outcome.
 *
 * - `none` — nothing happened worth reporting (applied, or still in flight).
 * - `conflict` — §5.1: show both values and let the user choose. Never a toast.
 * - `notify` — tell the user, with `messageKey` explaining what.
 * - `converge` — not a user-facing error: the task already exists, which is the
 *   state the command was trying to reach.
 */
export type TaskFailureAction = 'none' | 'conflict' | 'notify' | 'converge'

export interface TaskFailureVerdict {
  action: TaskFailureAction
  /** i18n key for the message, when `action` is `notify`. */
  messageKey?: string
  /**
   * True when the cause is a defect in this client rather than anything the
   * user did — a malformed task id (§5.6), an operation the service does not
   * know. The user still gets a message, because their change visibly reverted
   * and silence would be the worse failure; the *specifics* go to the log,
   * where someone can act on them.
   */
  isClientBug?: boolean
  /** Never retry automatically. Present so a caller cannot infer otherwise. */
  retryable: false
}

const GENERIC = 'tasks.errors.command.generic'

/**
 * The §4.5 table, as data.
 *
 * Anything absent falls through to {@link GENERIC} — the tolerant-reader rule
 * from `TaskEditErrorCode`. A newer service can answer with a class this build
 * predates, and an unrecognised code must reach "something went wrong" rather
 * than fall off the end of a dispatch.
 */
const BY_CODE: Partial<Record<TaskEditErrorCode, TaskFailureVerdict>> = {
  stale: { action: 'conflict', retryable: false },
  policy: { action: 'notify', messageKey: 'tasks.errors.command.policy', retryable: false },
  unauthorized: {
    action: 'notify',
    messageKey: 'tasks.errors.command.unauthorized',
    retryable: false,
  },
  task_not_found: {
    action: 'notify',
    messageKey: 'tasks.errors.command.task_not_found',
    retryable: false,
  },
  already_created: { action: 'converge', retryable: false },
  invalid_task_id: {
    action: 'notify',
    messageKey: GENERIC,
    isClientBug: true,
    retryable: false,
  },
  unknown_op: { action: 'notify', messageKey: GENERIC, isClientBug: true, retryable: false },
  // MYAC-1266. The thread moved under the author — someone withdrew the comment
  // they were rewording, most likely themselves in another tab. Notified rather
  // than surfaced as a conflict: there is nothing to reconcile, the live
  // subscription has already corrected what is on screen.
  comment_not_found: {
    action: 'notify',
    messageKey: 'tasks.errors.command.comment_not_found',
    retryable: false,
  },
}

/**
 * The parts of an outcome that deciding its meaning actually depends on.
 *
 * Deliberately narrower than {@link SequencedOutcome}: neither function below
 * has ever read `baseSeq`, and requiring it forced callers without one to
 * invent it. A `task_create` has no `base_seq` — there is no prior revision for
 * it to be stale against (§4.3) — so the honest signature is the one that asks
 * for what it reads. `SequencedOutcome` still satisfies this structurally, so
 * every existing caller is unaffected.
 */
export interface ClassifiableOutcome {
  status: SequencedOutcome['status']
  errorCode?: TaskEditErrorCode
  error?: string
  commandId: string
}

/**
 * Decide what an outcome means, without doing anything about it.
 *
 * Pure, so the §4.5 table can be tested as a table rather than through a
 * toast store.
 */
export function classifyOutcome(outcome: ClassifiableOutcome): TaskFailureVerdict {
  // Applied. Nothing to say.
  if (outcome.status === 'done') return { action: 'none', retryable: false }

  // Neither success nor failure: 3.2d owns this, as a pending state rather than
  // a message. Reporting it as an error would claim the edit was lost, which is
  // exactly what we do not know.
  if (outcome.status === 'unresolved') return { action: 'none', retryable: false }

  // An earlier command in this chain already failed and was already reported.
  // A second message for the same cause is the cascade §5.1 removes.
  if (outcome.status === 'discarded') return { action: 'none', retryable: false }

  return (
    BY_CODE[outcome.errorCode ?? 'unknown'] ?? {
      action: 'notify',
      messageKey: GENERIC,
      retryable: false,
    }
  )
}

/**
 * Classify an outcome **and** act on it: toast the user, log a client bug.
 *
 * Returns the verdict so a caller can handle the one thing this cannot do for
 * it — opening the conflict surface, which needs both values and only the
 * caller has them.
 */
export function reportOutcome(
  outcome: ClassifiableOutcome,
  /**
   * `quiet` withholds the TOAST only (MYAC-1061). Classification, logging and
   * the returned verdict are unchanged, so a caller that suppresses the message
   * still learns everything this function knows — and every log line below
   * still lands, which is what keeps a silenced refusal diagnosable.
   *
   * The one caller is bulk reject, which fires N commands and owns a better
   * message than N copies of the same toast. Default off: a single operation
   * reports itself.
   */
  opts: { quiet?: boolean } = {},
): TaskFailureVerdict {
  const verdict = classifyOutcome(outcome)
  const { t } = i18n.global

  // Exactly one line per outcome, and every one of them carries
  // `outcome.error` — the apply layer's own explanation of the refusal.
  //
  // **Why that matters more than it looks.** The toast is a canned string, and
  // has to be: the apply layer's message is English-only and technical, so it
  // belongs in a log rather than in front of a user. But a canned string can
  // describe the wrong thing. `policy` today covers both "your change breaks a
  // rule" and "the stored task holds a value this service cannot parse" — and
  // for the second, "That change is not allowed on this task" is actively
  // misleading: the task is unreadable, and nothing the user did was
  // disallowed. Discarding `outcome.error` leaves support with a complaint and
  // no way to tell those apart.
  //
  // (Splitting `policy` into two classes is a spec change, tracked separately.
  // Keeping the explanation is what makes the current one diagnosable.)
  const detail = outcome.error ?? '(no detail)'
  const prefix = `task command ${outcome.commandId}`

  if (verdict.isClientBug) {
    // A defect in this client — actionable by us, so it is an error.
    logger.error(`${prefix}: ${outcome.errorCode} - client defect, not a user error`, detail)
  } else if (verdict.messageKey === GENERIC && verdict.action === 'notify') {
    // The dispatch fell through: a class this build does not know, or a refusal
    // with no code at all. `useTaskCommands` already named the wire value if
    // there was one — this says the table had no row for it, and the two
    // together locate the gap without a repro.
    logger.warn(`${prefix}: no dispatch for error_code "${outcome.errorCode}"`, detail)
  } else if (verdict.action === 'notify' || verdict.action === 'conflict') {
    // A rule working, or a real conflict. Not a defect, so `info` — but the
    // explanation is kept, because the canned message the user saw may not
    // describe what actually happened.
    logger.info(`${prefix}: refused ${outcome.errorCode}`, detail)
  } else if (verdict.action === 'converge') {
    // Not an error: the task the command wanted already exists, which is the
    // state it was trying to reach. Logged only so a burst of these is visible
    // if a create path ever starts double-submitting.
    logger.info(`${prefix}: already created, converging`)
  }

  if (verdict.action === 'notify' && verdict.messageKey && !opts.quiet) {
    useAppStore().toast(t('common.error'), t(verdict.messageKey), 'error')
  }

  return verdict
}
